import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VideoTestimonialSection = ({ onWhatsAppClick }) => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const [activeVideo, setActiveVideo] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const videoTestimonials = [
    {
      id: 1,
      name: "Rahul Sharma",
      location: "Mumbai, Maharashtra",
      thumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=center",
      duration: "2:45",
      title: "From ₹500 to ₹50,000 in One Month",
      preview: "Rahul shares his incredible journey with Nitin and how personalized WhatsApp support helped him become a consistent winner.",
      videoUrl: "#",
      winAmount: "₹50,000",
      gameType: "Teen Patti"
    },
    {
      id: 2,
      name: "Priya Patel",
      location: "Ahmedabad, Gujarat",
      thumbnail: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=300&fit=crop&crop=center",
      duration: "3:12",
      title: "Why I Trust Nitin with My Gaming",
      preview: "Priya explains why she chose Nitin over other platforms and how the instant payout system changed her gaming experience.",
      videoUrl: "#",
      winAmount: "₹75,000",
      gameType: "Rummy"
    },
    {
      id: 3,
      name: "Amit Kumar",
      location: "Delhi, NCR",
      thumbnail: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=300&fit=crop&crop=center",
      duration: "2:28",
      title: "24/7 Support That Actually Works",
      preview: "Amit talks about how Nitin\'s WhatsApp support team helped him during a technical issue and ensured he never missed a game.",
      videoUrl: "#",
      winAmount: "₹1,25,000",
      gameType: "Poker"
    },
    {
      id: 4,
      name: "Sneha Reddy",
      location: "Hyderabad, Telangana",
      thumbnail: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=300&fit=crop&crop=center",
      duration: "3:45",
      title: "My Biggest Win Story",
      preview: "Sneha shares her biggest win story and how Nitin\'s fair play guarantee gave her confidence to play bigger games.",
      videoUrl: "#",
      winAmount: "₹2,50,000",
      gameType: "Andar Bahar"
    }
  ];

  const behindScenes = [
    {
      id: 1,
      title: "WhatsApp Support Center",
      description: "Take a look inside our 24/7 WhatsApp support center where our gaming experts provide personalized assistance to every player.",
      thumbnail: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=300&fit=crop&crop=center",
      duration: "1:30"
    },
    {
      id: 2,
      title: "Founder\'s Message",
      description: "Our founder shares the vision behind Nitin and why we\'re committed to being India\'s most trusted gaming platform.",
      thumbnail: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop&crop=center",
      duration: "2:15"
    }
  ];

  const handleVideoPlay = (videoId) => {
    setActiveVideo(videoId);
    // In a real implementation, this would open a video modal or player
    console.log(`Playing video: ${videoId}`);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="Play" size={32} color="white" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
            Real Stories, <span className="text-gradient-royal">Real Winners</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear directly from our players about their winning experiences, exceptional support, and why they trust Nitin for their gaming journey.
          </p>
        </div>

        {/* Video Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {videoTestimonials?.map((video, index) => (
            <div
              key={video?.id}
              className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="bg-white rounded-2xl shadow-subtle border border-gray-100 overflow-hidden hover:shadow-royal transition-all duration-300 hover-lift">
                {/* Video Thumbnail */}
                <div className="relative">
                  <Image
                    src={video?.thumbnail}
                    alt={video?.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <button
                      onClick={() => handleVideoPlay(video?.id)}
                      className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 hover:scale-110"
                    >
                      <Icon name="Play" size={24} color="var(--color-primary)" />
                    </button>
                  </div>
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
                    {video?.duration}
                  </div>
                  <div className="absolute bottom-4 left-4 bg-gradient-royal text-white px-3 py-1 rounded-full text-sm font-bold">
                    Won {video?.winAmount}
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} color="white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{video?.name}</h3>
                      <p className="text-sm text-gray-500">{video?.location}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-bold rounded-full">
                        {video?.gameType}
                      </span>
                    </div>
                  </div>
                  
                  <h4 className="text-xl font-heading font-bold text-gray-900 mb-2">{video?.title}</h4>
                  <p className="text-gray-600 text-sm mb-4">{video?.preview}</p>
                  
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleVideoPlay(video?.id)}
                      className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-300"
                    >
                      <Icon name="Play" size={16} />
                      <span className="text-sm font-medium">Watch Story</span>
                    </button>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Icon name="Eye" size={16} color="var(--color-gray-400)" />
                        <span className="text-xs text-gray-400">2.5K views</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="ThumbsUp" size={16} color="var(--color-success)" />
                        <span className="text-xs text-gray-400">98%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Behind the Scenes */}
        <div className={`mb-16 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Behind the <span className="text-gradient-royal">Royal Scenes</span>
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {behindScenes?.map((video, index) => (
              <div
                key={video?.id}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 4) * 200}ms` }}
              >
                <div className="bg-white rounded-2xl shadow-subtle border border-gray-100 overflow-hidden hover:shadow-royal transition-all duration-300 hover-lift">
                  <div className="relative">
                    <Image
                      src={video?.thumbnail}
                      alt={video?.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <button
                        onClick={() => handleVideoPlay(video?.id)}
                        className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-300"
                      >
                        <Icon name="Play" size={20} color="var(--color-primary)" />
                      </button>
                    </div>
                    <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                      {video?.duration}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{video?.title}</h4>
                    <p className="text-gray-600 text-sm">{video?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`text-center transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
              Ready to Write Your Success Story?
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of winners who trust Nitin for their gaming journey. Our personalized WhatsApp support ensures you get the guidance you need to succeed.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={onWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-gradient-royal hover:shadow-glow animate-pulse-golden hover:animate-none"
              >
                Start Your Winning Journey
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                Watch More Stories
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex flex-wrap justify-center gap-6 mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <Icon name="Play" size={16} color="var(--color-primary)" />
                <span className="text-sm text-gray-600">50+ Video Testimonials</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} color="var(--color-warning)" />
                <span className="text-sm text-gray-600">4.9/5 Average Rating</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} color="var(--color-success)" />
                <span className="text-sm text-gray-600">10K+ Happy Players</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoTestimonialSection;